import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
  viewChild,
} from '@angular/core';

import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  WarehouseReceiptFormData,
  WarehouseReceiptFormGroup,
  WarehouseReceiptProductFormData,
  WarehouseReceiptProductFormGroup,
} from '../../models/warehouse-receipt-form-data';
import {
  ArrayValidator,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengTextareaDirective,
} from '@web/shared/ui';
import { InputTextModule } from 'primeng/inputtext';
import {
  getLastIndexOfArray,
  Guid,
  isNil,
  Nillable,
  once,
  valueOrDefault,
} from '@web/utils';
import { WarehouseOwnerDataSourceService } from '../../services/warehouse-owner-data-source.service';
import { Button } from 'primeng/button';
import { QuantityUnit } from '@web/shared/module/measurement';
import { Accordion, AccordionModule } from 'primeng/accordion';
import { WarehouseFilesUploadComponent } from '../../containers/warehouse-files-upload/warehouse-files-upload.component';
import { WarehouseProductType } from '../../enums/warehouse-product-type';
import { CheckboxModule } from 'primeng/checkbox';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { Textarea } from 'primeng/textarea';
import { DatePicker } from 'primeng/datepicker';
import { WarehouseReceiptFormProductPanelComponent } from '../warehouse-receipt-form-product-panel/warehouse-receipt-form-product-panel.component';

@Component({
  selector: 'app-warehouse-receipt-form',
  imports: [
    AccordionModule,
    AutoCompleteModule,
    Button,
    CheckboxModule,
    DatePicker,
    FormControlComponent,
    InputTextModule,
    PrimengButtonDirective,
    PrimengTextareaDirective,
    ReactiveFormsModule,
    Textarea,
    WarehouseFilesUploadComponent,
    WarehouseReceiptFormProductPanelComponent,
  ],
  templateUrl: './warehouse-receipt-form.component.html',
  styleUrl: './warehouse-receipt-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseReceiptFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);
  /* prettier-ignore */
  private readonly warehouseOwnerDataSource = inject(WarehouseOwnerDataSourceService);

  public readonly data = input<Nillable<WarehouseReceiptFormData>>();
  public readonly submitSuccessEvent = output<WarehouseReceiptFormData>();
  public readonly fileUploadVisibility = input(false);

  protected readonly accordion = viewChild(Accordion);
  protected readonly form = this.buildForm();
  protected readonly valueOrDefault = valueOrDefault;

  protected activeProductIndex = 0;

  public ngOnInit(): void {
    this.fillByData();
  }

  public handleSubmitClick(): void {
    const data = this.form.getRawValue();

    this.submitSuccessEvent.emit(
      new WarehouseReceiptFormData({
        receiptedAt: data.receiptedAt,
        fileIds: data.fileIds,
        comment: data.comment,
        products: data.products.map(
          (product) =>
            new WarehouseReceiptProductFormData({
              batchNumber: product.batchNumber,
              brand: product.brand,
              durabilityInformation: product.durabilityInformation,
              id: product.id,
              name: product.name,
              ownerId: product.ownerId,
              promotion: product.promotion,
              quantity: product.quantity,
              sku: product.sku,
              type: product.type,
              unit: product.unit,
            }),
        ),
      }),
    );
  }

  public getProductFormGroups(): WarehouseReceiptProductFormGroup[] {
    return this.form.controls.products.controls;
  }

  public handleProductRemoveClick(index: number): void {
    this.form.controls.products.removeAt(index);
    this.activeProductIndex = this.form.controls.products.length - 1;
  }

  public handleProductAddClick(): void {
    this.form.controls.products.push(this.buildProductForm());
    this.activeProductIndex = this.form.controls.products.length - 1;
  }

  private buildForm(): WarehouseReceiptFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      receiptedAt: this.builder.nonNullable.control(new Date(), [Validators.required]),
      comment: this.builder.control<Nillable<string>>(null, [Validators.maxLength(WarehouseReceiptFormData.MaxCommentLength)]),
      products: this.builder.nonNullable.array<WarehouseReceiptProductFormGroup>([], [ArrayValidator.min(WarehouseReceiptFormData.MinProductCount)]),
      fileIds: this.builder.control<Nillable<Guid[]>>(null)
    })
  }

  private buildProductForm(
    data?: WarehouseReceiptProductFormData,
  ): WarehouseReceiptProductFormGroup {
    /* prettier-ignore */
    const formGroup = this.builder.group({
      id: this.builder.control<Nillable<Guid>>(data?.id),
      ownerId: this.builder.control<Nillable<Guid>>(data?.ownerId, [Validators.required]),
      type: this.builder.control<Nillable<WarehouseProductType>>(valueOrDefault(data?.type, WarehouseProductType.Cosmetic), [Validators.required]),
      sku: this.builder.control<Nillable<string>>(data?.sku, [Validators.required, Validators.maxLength(WarehouseReceiptProductFormData.MaxSkuLength)]),
      batchNumber: this.builder.control<Nillable<string>>(data?.batchNumber, [Validators.maxLength(WarehouseReceiptProductFormData.MaxBatchNumberLength)]),
      name: this.builder.control<Nillable<string>>(data?.name, [Validators.required, Validators.maxLength(WarehouseReceiptProductFormData.MaxNameLength)]),
      brand: this.builder.control<Nillable<string>>(data?.brand, [Validators.required]),
      promotion: this.builder.control<Nillable<boolean>>(valueOrDefault(data?.promotion, false)),
      unit: this.builder.nonNullable.control(valueOrDefault(data?.unit, QuantityUnit.Piece), [Validators.required]),
      quantity: this.builder.control<Nillable<number>>(data?.quantity, [Validators.required, Validators.min(WarehouseReceiptProductFormData.MinQuantity)]),
      durabilityInformation: this.builder.control<Nillable<string>>(data?.durabilityInformation, [Validators.maxLength(WarehouseReceiptProductFormData.MaxDurabilityInformationLength)]),
    });

    if (isNil(data?.ownerId)) {
      this.fillOwnerByDefaultValueAsync(formGroup.controls.ownerId);
    }

    return formGroup;
  }

  private fillOwnerByDefaultValueAsync(
    ownerFormControl: FormControl<Nillable<Guid>>,
  ): void {
    this.warehouseOwnerDataSource
      .fetchCollectionAsSelectItems()
      .pipe(once())
      .subscribe((items) => {
        const [first] = items;

        ownerFormControl.setValue(first.value);
      });
  }

  private fillByData(): void {
    const data = this.data();

    if (isNil(data)) {
      this.form.controls.products.push(this.buildProductForm());

      return;
    }

    const products = valueOrDefault(data.products, []);

    products.forEach((product) =>
      this.form.controls.products.push(this.buildProductForm(product)),
    );

    this.activeProductIndex = getLastIndexOfArray(products);

    this.form.patchValue(
      {
        receiptedAt: data?.receiptedAt,
        fileIds: data?.fileIds,
        comment: data?.comment,
      },
      { emitEvent: false },
    );
  }
}
